// ── SCO / ECO BAND BOUNDARIES ARE A CROP-YEAR RULE ───────────────────────────
//
// SCO and ECO stack on top of MPCI, and where one stops and the next starts
// MOVED. Every surface that draws, labels or triggers those bands has to ask
// WHICH CROP YEAR it is talking about, because a 2026 policy and a 2027 policy
// are two different products sitting in the same picture.
//
//   CY2026 and earlier   SCO: coverage level → 86      ECO: 86 → 95   (9 pts)
//   CY2027 and later     SCO: coverage level → 90      ECO: 90 → 95   (5 pts)
//
// SOURCED, per rule zero. RMA PM-26-036, "Enhanced Coverage Option, Margin
// Coverage Option, and Supplemental Coverage Option Endorsement Changes
// Effective for the 2027 and Succeeding Crop Years" — the FCIC Board approved
// the ECO/MCO coverage-level changes on 2025-08-15 to match the SCO change the
// One Big Beautiful Bill Act made (MGR-25-006, OBBBA signed 2025-07-04), which
// raised SCO's maximum coverage level from 86 to 90 percent. RMA's own wording:
// the SCO policy is updated "for the 2027 crop year", and for CY2026 "insureds
// can cover this band of insurance with ECO" at the interim 80% ECO subsidy.
//
// AND MEASURED AGAINST THE AIP'S OWN NUMBERS, not taken on the bulletin's word.
// Back-solving the band out of stored liability —
//
//     band = endorsement liability ÷ (MPCI liability ÷ coverage level) ÷ elected %
//
// — over the live CY2026 book returns 0.0900 on every ECO row and exactly
// (0.86 − coverage level) on every SCO row: 0.26 at 60%, 0.16 at 70%, 0.11 at
// 75%, 0.06 at 80%, 0.01 at 85%. Worked example, KAST FARMS Orleans wheat 2026,
// 458.8 ac at 80% RP: MPCI liability $167,018 → expected crop value $455.04/ac;
// SCO $12,526 = $27.30/ac = 6 points; ECO $18,790 = $40.95/ac = 9 points; the
// three layers sum to $432.28/ac = 95% of expected revenue exactly.
//
// **IT IS A CROP-YEAR RULE AND NOT A WHEAT RULE.** The same back-solve over
// CY2026 CORN and SOYBEANS returns the same 0.0900 ECO band and the same
// 0.86 − level SCO band, so spring 2026 crops sit on 86 exactly as fall 2026
// wheat does. Corn and soy renewals inherit this without a second table.
//
// THE YEAR IS REQUIRED AND THERE IS NO DEFAULT. Defaulting it is precisely how
// a 2026 column comes to be drawn with 2027 geometry — which is the defect this
// module exists to fix. Every caller has the crop year in hand; a missing one is
// a programming error, so it throws rather than guessing and being wrong on a
// document a grower reads.

// The FIRST crop year written on the new structure. RMA: "effective for the 2027
// and succeeding crop years".
export const SCO_ECO_BAND_CHANGE_YEAR = 2027;

// Decimal band boundaries for a crop year. `ecoTop` has never moved.
export function scoEcoBands(cropYear) {
  const y = Number(cropYear);
  // `Number.isFinite` alone is NOT the guard, and the checker is what found that:
  // `Number(null)` and `Number("")` are both 0 — finite, so a null crop year
  // sailed through and was silently answered on the 86% lattice. A range test is
  // the honest one; it also catches a coverage level (0.75) or a FIPS code
  // arriving where a year belongs, which is the likelier slip at a call site.
  if (!Number.isFinite(y) || y < 1900 || y > 2200) {
    throw new Error(`scoEcoBands: crop year is required and must be a real year (got ${JSON.stringify(cropYear)}) — a band drawn on an assumed year is the defect this rule exists to prevent`);
  }
  const top = y >= SCO_ECO_BAND_CHANGE_YEAR ? 0.90 : 0.86;
  return { cropYear: y, scoTop: top, ecoBottom: top, ecoTop: 0.95 };
}

// The four revenue slices of a coverage stack, bottom → top, for one crop year
// and one MPCI coverage level. ONE definition, so the on-screen bar, the printed
// bar row and the printed single stack cannot disagree about what a layer covers.
// `lo`/`hi` are fractions of expected revenue; `band` is the label.
//
// A coverage level ABOVE the SCO top (85% MPCI against a CY2026 86% top) leaves a
// 1-point SCO band, and a level at or above it leaves none — the zero-width slice
// is dropped by the caller's own `hi > lo` filter, which is the honest answer: a
// grower there has no SCO band to sell.
export function scoEcoSlices(cropYear, cl) {
  const b = scoEcoBands(cropYear);
  const lvl = Math.round(cl * 100);
  return [
    { key: "MPCI", lo: 0,           hi: cl,          band: `RP ${lvl}%` },
    { key: "SCO",  lo: cl,          hi: b.scoTop,    band: `${lvl}→${Math.round(b.scoTop * 100)}%` },
    { key: "ECO",  lo: b.ecoBottom, hi: b.ecoTop,    band: `${Math.round(b.ecoBottom * 100)}→${Math.round(b.ecoTop * 100)}%` },
    { key: "open", lo: b.ecoTop,    hi: 1.00,        band: `top ${Math.round((1 - b.ecoTop) * 100)}%` },
  ];
}

// Names a band selection in one place, so a card header, its intro and its
// footnote can never disagree about which bands the numbers on screen cover.
// Single object argument on purpose — nothing here can be dropped at a call site.
export function scoEcoBandLabel({ cropYear, sco, eco, baseClPct = null }) {
  const b = scoEcoBands(cropYear);
  const sTop = Math.round(b.scoTop * 100);
  const s = baseClPct != null ? `SCO ${baseClPct}→${sTop}` : `SCO→${sTop}`;
  const e = `ECO ${Math.round(b.ecoBottom * 100)}→${Math.round(b.ecoTop * 100)}`;
  if (sco && eco) return s + " + " + e;
  if (sco) return s;
  if (eco) return e;
  return "no band selected";
}

// ── THE COVERAGE PERCENTAGE: HOW MUCH OF THE BAND A GROWER BUYS ──────────────
//
// The band above says WHERE SCO and ECO sit. This says how much of that band is
// insured, and it is a separate election on each endorsement.
//
// SOURCED, per rule zero — both CY2027 endorsements, section 2(c), identically:
//   "You may select a coverage percentage, from 50 percent to 100 percent, when
//    you elect coverage under this Endorsement. The default coverage percentage
//    is 100 percent."
// and section 6(a)(3): protection = coverage range x expected crop value x the
// coverage percentage you selected; section 7(b): premium = that protection x
// the premium rate. So the percentage multiplies protection BEFORE the premium
// is rated, and the rate itself does not depend on it — a 50% ECO is half the
// coverage at half the premium (RMA's own example: $840,000 x 0.09 x 0.80 =
// $60,480). SCO has carried it since CY2019 (MGR-18-006); the CY2027 SCO text is
// 27-SCO, the ECO text is the ECO Endorsement. RMA publishes no increment, only
// the range, so nothing here rounds an election to a step.
//
// MEASURED AGAINST THE AIP'S OWN NUMBERS: on an endorsement row,
// crop_coverage.price_election_pct is NOT a price election, it is this
// percentage. Over the live CY2026 book, endorsement liability ÷ expected crop
// value ÷ that column lands on exactly the 9-point ECO band (0.0900) and on
// 0.86 − level for SCO — on corn, soybeans and wheat alike. Reduced elections
// held in CY2026: ECO corn 10, soybeans 12, wheat 19; SCO corn 1.
//
// ⚠ THREE RULES, because this value arrives in two units and can be absent:
//   * the ENGINE takes a FRACTION (0.50–1.00). A percent handed to it by
//     mistake (50) is REFUSED, not read as 50x the coverage.
//   * a surface takes a PERCENT (50–100). Blank means the RMA default, 100.
//   * a value outside RMA's range is refused (null), never clamped: it is an
//     election RMA does not sell, and quoting its nearest neighbour would print
//     coverage nobody chose.
export const AREA_COVERAGE_PCT_MIN = 50;
export const AREA_COVERAGE_PCT_MAX = 100;
export const AREA_COVERAGE_PCT_DEFAULT = 100;
const _PCT_TOL = 1e-9;

// Engine input. `undefined` is "not supplied" and takes the RMA default; every
// other value must be a fraction RMA sells.
export function areaCoverageFraction(v) {
  if (v === undefined) return AREA_COVERAGE_PCT_DEFAULT / 100;
  if (v === null || v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  if (n < AREA_COVERAGE_PCT_MIN / 100 - _PCT_TOL || n > AREA_COVERAGE_PCT_MAX / 100 + _PCT_TOL) return null;
  return n;
}

// A percent from a control or a stored override. Blank (null, "", 0) is the
// unset control and takes the RMA default of 100.
export function areaCoveragePct(pct) {
  if (pct == null || pct === "") return AREA_COVERAGE_PCT_DEFAULT;
  const n = Number(pct);
  if (!Number.isFinite(n)) return null;
  if (n === 0) return AREA_COVERAGE_PCT_DEFAULT;
  if (n < AREA_COVERAGE_PCT_MIN - _PCT_TOL || n > AREA_COVERAGE_PCT_MAX + _PCT_TOL) return null;
  return n;
}

// Percent -> the fraction the engine takes. A refused percent stays refused.
export function areaCoverageFractionOfPct(pct) {
  const p = areaCoveragePct(pct);
  return p == null ? null : p / 100;
}

// The percentage a grower HOLDS, read off their SCO/ECO crop_coverage row.
// A row that states none is on the RMA default; a stated value outside the
// range is returned as null so the caller can refuse rather than guess.
export function endorsementCoveragePct(row) {
  if (!row) return null;
  const v = row.price_election_pct;
  if (v == null || v === "") return AREA_COVERAGE_PCT_DEFAULT;
  const n = Number(v);
  if (!Number.isFinite(n) || n < AREA_COVERAGE_PCT_MIN / 100 - _PCT_TOL || n > AREA_COVERAGE_PCT_MAX / 100 + _PCT_TOL) return null;
  return Math.round(n * 10000) / 100;
}

// "ECO" at the default, "ECO 50%" below it. One formatter, so a column header,
// a banner and a printed row cannot disagree about which election they show.
export function areaCoverageLabel(endorsement, pct) {
  const p = areaCoveragePct(pct);
  if (p == null || p >= AREA_COVERAGE_PCT_MAX) return endorsement;
  return `${endorsement} ${Number.isInteger(p) ? p : p.toFixed(2)}%`;
}
